import React, { useEffect, useState, useRef } from "react";
import SearchTermExtended from "modules/Content/Search/Components/SearchTermExtended";
import SearchTermsContainer from "modules/Content/Search/Components/SearchTermsContainer";
import { ReactComponent as ReloadIcon } from "../../../assets/img/icons/reload.svg";
import { ReactComponent as SearchCross } from "../../../assets/img/icons/SearchCross.svg";
import styled from "styled-components";

interface IKeywordGenericSearch {
  setCampaignKeywords?: any;
}

const KeywordGenericSearch: React.FC<any> = (props) => {
  const searchNode = useRef();
  const { editable, smartSearchTerms, onSearch } = props;
  const [searchTerms, setSearchTerms] = useState([]);
  const [extendedSearchTerms, setExtendedSearchTerms] = useState([]);
  const [showReset, setShowReset] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showHint, setShowHint] = useState(false);

  // common util to split search terms and populate extended/main search field
  const updateSearchTerms = (currentSearchTerms: any = []) => {
    // set search terms count based on characters
    // convert all to small case
    currentSearchTerms = currentSearchTerms.map((v: string) => v.toLowerCase().replace(" and ", " AND "));
    // add only unique terms
    currentSearchTerms = [...new Set(currentSearchTerms)];

    let searchQuery = "";
    let searchTermsSplitter = extendedSearchTerms.length === 0 ? 2 : searchTerms.length;

    currentSearchTerms.every((element: any, index: number) => {
      searchQuery += element;

      if (searchQuery.length > 40 && extendedSearchTerms.length == 0) {
        searchTermsSplitter = index;
        return false;
      }
      return true;
    });
    // show cross on search if search terms exists
    setShowReset(currentSearchTerms.length > 0);
    if (currentSearchTerms.length === 0) {
      resetSearch();
    } else if (currentSearchTerms.length < searchTermsSplitter) {
      setSearchTerms(currentSearchTerms);
      setExtendedSearchTerms([]);
    } else {
      setSearchTerms(currentSearchTerms.slice(0, searchTermsSplitter));
      setExtendedSearchTerms(currentSearchTerms.slice(searchTermsSplitter, currentSearchTerms.length));
    }
  };

  // on , or enter click add search terms to current array and update article results
  const addSearchTerm = (event: any) => {
    if (event.key === "Enter" || event.key === ",") {
      let searchText = event.target.value;
      let currentSearchTerms = searchText.split(",");
      currentSearchTerms = currentSearchTerms
        .map((word: string) => word.trim())
        .map((word: string) => word.toLowerCase().replace(" and ", " AND "))
        .filter((word: string) => word !== "");

      //fetch all search terms extended ones and in main bar
      let allSearchTerms = Array.from(new Set([...searchTerms, ...extendedSearchTerms, ...currentSearchTerms]));
      updateSearchTerms(allSearchTerms);
      props.setCampaignKeywords(allSearchTerms);
      setSearchValue("");

      if (event.key === "Enter" || event.key === ",") {
        // update search results based on enter press
        onSearch(allSearchTerms);
      }
    }
  };

  // on cross click remove search term from field
  const removeSearchTerm = (searchTermId: any, isExtended: any) => {
    let tmpSearchTerms = isExtended ? extendedSearchTerms : searchTerms;
    // find search term based on id
    tmpSearchTerms = tmpSearchTerms.filter((s, id) => id !== searchTermId);
    tmpSearchTerms = isExtended ? searchTerms.concat(tmpSearchTerms) : tmpSearchTerms.concat(extendedSearchTerms);

    updateSearchTerms(tmpSearchTerms);
    props.setCampaignKeywords(tmpSearchTerms);
    //search on remove keywords
    setSearchValue("");
    onSearch(tmpSearchTerms);
  };

  const handleSearchFocus = (e) => {
    setShowHint(searchNode.current.contains(e.target)); // show search hint on search field click
  };
  useEffect(() => {
    if (smartSearchTerms) updateSearchTerms(smartSearchTerms);
    document.addEventListener("mousedown", handleSearchFocus);
    return () => {
      document.removeEventListener("mousedown", handleSearchFocus);
    };
  }, []);

  const reloadSearch = () => {
    let allSearchTerms = searchTerms.concat(extendedSearchTerms);
    onSearch(allSearchTerms);
  };

  const resetSearch = () => {
    //clear all arrays on reset click
    props.setCampaignKeywords([]);
    setSearchTerms([]);
    setExtendedSearchTerms([]);
    setSearchValue("");
    setShowReset(false);
    onSearch([]);
  };

  useEffect(() => {
    if (searchTerms?.length === 0) {
      if (smartSearchTerms?.length > 0) {
        updateSearchTerms(smartSearchTerms);
      }
    } else {
      if (searchTerms.length !== smartSearchTerms.length) {
        updateSearchTerms(smartSearchTerms);
      }
    }
  }, [smartSearchTerms]);

  return (
    <Wrapper>
      <div ref={searchNode} className="search-wraper">
        <div className="search-bar search-added">
          <span className="refresh-search">{<ReloadIcon onClick={reloadSearch} />}</span>

          <div className="search-terms d-flex flex-nowrap">
            <SearchTermsContainer
              editable={editable}
              searchTerms={searchTerms}
              onRemove={removeSearchTerm}
              isExtended={false}
            />
            {extendedSearchTerms.length > 0 && <SearchTermExtended count={extendedSearchTerms.length} />}

            <input
              type="text"
              name="search"
              onChange={(e) => (e.target.value !== "," ? setSearchValue(e.target.value) : null)}
              onKeyPress={(e) => addSearchTerm(e)}
              className="search-input"
              placeholder={
                searchTerms.length === 0 && extendedSearchTerms.length === 0
                  ? "Search for keywords, ideas, concepts or companies…"
                  : ""
              }
              value={searchValue}
              autoComplete="off"
              onFocus={(e) => setShowHint(true)}
            />
          </div>
          {/* <AdvanceSearch show="d-block" /> */}
          {showReset && (
            <span className="clear-search">
              <SearchCross onClick={resetSearch} />
            </span>
          )}
        </div>
        {extendedSearchTerms.length > 0 && showHint && (
          <div className={`search-results show showExtended`}>
            <ul className={`results-list-term extended`}>
              <div className="inner-wrapper">
                <SearchTermsContainer
                  searchTerms={extendedSearchTerms}
                  onRemove={removeSearchTerm}
                  isExtended={true}
                />
              </div>
            </ul>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default KeywordGenericSearch;

const Wrapper = styled.div<any>`
  .search-wraper {
    position: relative;
    width: 100%;
    z-index: 3;
    .search-results {
      border-radius: 22px;
      position: absolute;
      background: rgb(247, 247, 247);
      top: -80px;
      left: 0;
      float: left;
      width: 100%;
      /* border: 1px solid #dadada; */
      box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.12);
      overflow: hidden;
      transition: 0.5s;
      z-index: -1;
      opacity: 0;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        top: -65px;
      }
      &.showExtended {
        top: 00px;
      }
      &.show {
        opacity: 1;
        z-index: 1;
        visibility: visible;
      }
      ul.results-list {
        background: #fff;
        padding: 30px 20px 10px 55px;
        margin: 0;
        list-style: none;
        float: left;
        display: block;
        width: 100%;
        transition: 0.5s;

        li {
          color: #5e7079;
          font-size: 16px;
          line-height: 34px;
          padding: 0;
          list-style: none;
          width: 100%;
          display: flex;
          justify-content: space-between;
          cursor: pointer;
          transition: 0.5s;
          span {
            color: #c5c5c5;
            font-size: 12px;
            font-family: "robotoregular", sans-serif;
          }
        }
      }
      ul.results-list-term {
        background: #fff;
        padding: 30px 20px 10px 55px;
        margin: 0;
        border-bottom-left-radius: 20px;
        border-bottom-right-radius: 20px;
        list-style: none;
        float: left;
        display: block;
        width: 100%;
        transition: 0.5s;
        &.empity {
          padding: 20px 20px 0 55px;
        }
        .inner-wrapper {
          > div {
            margin-bottom: 4px;
          }
          max-height: 280px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #001760 #efefef;
          ::-webkit-scrollbar {
            width: 5px;
            height: 5px;
            border-radius: 2px;
          }
          /* Track */
          ::-webkit-scrollbar-track {
            background: #efefef;
            margin-top: 10px;
            margin-bottom: 15px;
          }
          /* Handle */
          ::-webkit-scrollbar-thumb {
            background: #001760;
          }
          /* Handle on hover */
          ::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        }
        li {
          color: #5e7079;
          font-size: 16px;
          line-height: 34px;
          padding: 0;
          list-style: none;
          width: 100%;
          display: flex;
          justify-content: space-between;
          cursor: pointer;
          transition: 0.5s;
          span {
            color: #c5c5c5;
            font-size: 12px;
            font-family: "robotoregular", sans-serif;
          }
        }
      }
      ul.extended {
        margin-top: 10px;
      }
      .relevant-theme,
      .relevant-company {
        border-top: 1px solid #dadada;
        padding: 10px 40px 30px 40px;
        background: #f7f7f7;
        float: left;
        width: 100%;
        transition: 0.5s;
        h3 {
          margin-bottom: 10px;
          color: #424b50;
          font-weight: 400;
          font-family: "Roboto Slab", serif;
          font-size: 18px;
          line-height: 34px;
        }
      }
      .relevant-keywords {
        padding: 10px 40px 30px 40px;
        background: #f7f7f7;
        float: left;
        width: 100%;
        transition: 0.5s;
        div {
          margin-bottom: 4px;
        }
        h3 {
          margin-bottom: 10px;
          color: #424b50;
          font-weight: 400;
          font-family: "Roboto Slab", serif;
          font-size: 18px;
          line-height: 34px;
        }
      }
      .search-hint {
        background: #def6e6;
        padding: 18px 25px 36px 25px;
        border-radius: 22px 22px 0 0;
        float: left;
        width: 100%;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding: 12px 25px 28px 25px;
        }
        svg {
          float: left;
          width: 35px;
          height: 39px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 25px;
          }
        }
        ul {
          margin: 0;
          float: left;
          padding-left: 15px;
          padding-top: 5px;
          li {
            color: #09a43a;
            font-size: 14px;
            font-weight: 400;
            list-style: none;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
            }
            strong {
              font-weight: 700;
            }
          }
        }
      }
    }
  }
  .search-bar {
    border-radius: 27px;
    background: ${(props) => (props.editable === false ? "#F2F2F2" : "#FFFFFF")};
    border: 1px solid ${(props) => (props.editable === false ? "#F2F2F2" : "#dadada")};
    display: block;
    float: left;
    width: 100%;
    overflow: hidden;
    z-index: 7;
    position: relative;
    .search-terms {
      width: calc(100% - 95px);
      overflow: hidden;
      float: left;
      margin-bottom: 3px;
    }
    span.clear-search {
      float: right;
      margin-top: 8px;
      margin-right: 12px;
      transition: 0.5s;
      cursor: pointer;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin: 3px 8px 3px 8px;
        float: right;
      }
      svg {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 9px;
          height: auto;
        }
      }
    }
    span.refresh-search {
      padding-right: 5px;
      border-right: 1px solid #dddddd;
      margin: ${(props) => (props.editable === false ? 0 : "8px")};
      float: left;
      cursor: pointer;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 13px;
      }
      svg {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 14px;
          height: auto;
          vertical-align: bottom;
        }
      }
    }
    .search-input {
      float: left;
      border: none;
      outline: none;
      width: calc(100% - 30px);
      display: block;
      color: #c1c1c1;
      background-color: ${(props) => (props.editable === false ? "#F2F2F2" : "#FFFFFF")};
      font-weight: 400;
      font-size: 18px;
      margin-top: 5px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px;
        margin-top: 3px;
      }
      -webkit-box-shadow: 0 0 0 30px white inset !important;
      &::-webkit-input-placeholder {
        font-family: "robotothin_italic";
        color: #c1c1c1;
      }
      &:-moz-placeholder {
        font-family: "robotothin_italic";
        color: #c1c1c1;
      }
      &::-moz-placeholder {
        font-family: "robotothin_italic";
        color: #c1c1c1;
      }
      &:-ms-input-placeholder {
        font-family: "robotothin_italic";
        color: #c1c1c1;
      }
      &:hover,
      &:active,
      &:focus {
        border: none;
        outline: none;
      }
    }
  }
`;
